import { motion } from 'framer-motion';
import { revealUp } from '../lib/anim';
import { DESIGN_STYLES } from '../data/portfolio';

/** Small stylized preview that visually embodies each design style. */
function StylePreview({ variant, name }) {
    switch (variant) {
        case 'minimal':
            return (
                <div className="relative flex h-full w-full items-center justify-center bg-[#fafaf8]">
                    <div className="h-px w-24 bg-neutral-900" />
                    <span className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                        —
                    </span>
                </div>
            );
        case 'brutal':
            return (
                <div className="relative flex h-full w-full items-center justify-center overflow-hidden bg-[#f2f230]">
                    <span className="font-mono text-3xl font-black uppercase leading-none tracking-tighter text-neutral-900 md:text-4xl">
                        RAW*
                    </span>
                    <div className="absolute inset-3 border-[3px] border-neutral-900" />
                    <div className="absolute right-2 top-2 h-4 w-4 rounded-full bg-neutral-900" />
                </div>
            );
        case 'editorial':
            return (
                <div className="relative flex h-full w-full flex-col justify-between bg-[#111] p-5">
                    <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-neutral-500">
                        Vol. 03 — Winter
                    </span>
                    <div
                        className="text-4xl italic text-neutral-100 md:text-5xl"
                        style={{ fontFamily: 'Cormorant Garamond, Playfair Display, serif' }}
                    >
                        <span className="float-left mr-1 text-6xl font-medium leading-[0.8] md:text-7xl">
                            A
                        </span>
                        n editorial voice.
                    </div>
                </div>
            );
        case 'neumorph':
            return (
                <div className="flex h-full w-full items-center justify-center bg-[#e0e5ec]">
                    <div
                        className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#e0e5ec]"
                        style={{
                            boxShadow:
                                '9px 9px 20px #a3b1c6, -9px -9px 20px #ffffff',
                        }}
                    >
                        <div
                            className="h-8 w-8 rounded-full bg-[#e0e5ec]"
                            style={{
                                boxShadow:
                                    'inset 4px 4px 8px #a3b1c6, inset -4px -4px 8px #ffffff',
                            }}
                        />
                    </div>
                </div>
            );
        case 'glass':
            return (
                <div className="relative h-full w-full overflow-hidden">
                    <div
                        className="absolute inset-0"
                        style={{
                            background:
                                'radial-gradient(120% 100% at 20% 0%, #7c3aed 0%, #db2777 45%, #f59e0b 100%)',
                        }}
                    />
                    <div
                        className="absolute left-1/2 top-1/2 h-24 w-40 -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-white/40"
                        style={{
                            background: 'rgba(255,255,255,0.15)',
                            backdropFilter: 'blur(16px)',
                            WebkitBackdropFilter: 'blur(16px)',
                        }}
                    >
                        <div className="flex h-full w-full items-center justify-center font-mono text-[10px] uppercase tracking-[0.2em] text-white">
                            frost
                        </div>
                    </div>
                </div>
            );
        case 'max':
        default:
            return (
                <div
                    className="relative flex h-full w-full items-center justify-center overflow-hidden"
                    style={{
                        background:
                            'conic-gradient(from 180deg at 50% 50%, #ff006e, #fb5607, #ffbe0b, #8338ec, #3a86ff, #ff006e)',
                    }}
                >
                    <span
                        className="font-display text-4xl font-black italic text-white mix-blend-difference md:text-5xl"
                        style={{ transform: 'rotate(-6deg)' }}
                    >
                        {name?.toUpperCase() || 'MORE'}!
                    </span>
                </div>
            );
    }
}

function StyleCard({ style, i }) {
    return (
        <motion.article
            variants={revealUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            custom={i}
            data-testid={`style-card-${style.id}`}
            className="group flex flex-col overflow-hidden rounded-[3px] border border-border bg-white/[0.015] transition-colors duration-500 hover:bg-white/[0.04]"
        >
            <div className="relative aspect-[4/3] w-full overflow-hidden">
                <div className="absolute inset-0 transition-transform duration-700 group-hover:scale-[1.03]">
                    <StylePreview variant={style.variant} name={style.name} />
                </div>
                <span className="absolute left-4 top-4 rounded-full bg-black/60 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-white backdrop-blur-sm">
                    0{i + 1}
                </span>
            </div>
            <div className="flex flex-col gap-3 p-6 md:p-7">
                <div className="flex items-baseline justify-between gap-4">
                    <h3 className="font-display text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
                        {style.name}
                    </h3>
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                        {style.tag}
                    </span>
                </div>
                <p className="text-[15px] leading-relaxed text-muted-foreground">
                    {style.body}
                </p>
            </div>
        </motion.article>
    );
}

export default function Styles() {
    return (
        <section
            id="styles"
            data-testid="styles-section"
            className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-32"
        >
            <div className="mb-16 grid grid-cols-1 gap-8 md:grid-cols-12 md:items-end">
                <div className="md:col-span-8">
                    <span className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                        (Design Styles)
                    </span>
                    <h2 className="mt-6 font-display text-4xl font-extrabold leading-[0.92] tracking-tighter text-foreground md:text-6xl">
                        Ogni brand ha
                        <br />
                        la sua estetica.
                    </h2>
                </div>
                <p className="text-base leading-relaxed text-muted-foreground md:col-span-4">
                    Lavoro con linguaggi visivi differenti a seconda di ciò che il progetto
                    richiede. Ecco una selezione degli stili che padroneggio.
                </p>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {DESIGN_STYLES.map((s, i) => (
                    <StyleCard key={s.id} style={s} i={i} />
                ))}
            </div>
        </section>
    );
}
